import React, { useContext } from 'react'
import { useParams } from 'react-router-dom'

import LoaderWrapper from '@flatsy/core/Loader/Wrapper'
import Consent from '@flatsy/core/Consent'
import { FetchStatus } from '@flatsy/types/FetchStatus'
import FormGdpr from 'src/components/Gdpr/Gdpr'
import VisitorFormProgress from 'src/components/ProgressBar'
import QualificationHeader from 'src/components/QualificationHeader'
import QualificationLayout from 'src/components/QualificationLayout'
import QualificationSideBar from 'src/components/QualificationSideBar'
import QualificationQualificationSideBarMobile from 'src/components/QualificationSideBar/Mobile'
import NotFound from 'src/containers/NotFound'
import { useApplicationDetails } from 'src/hooks/ApplicationDetails'
import { useApplicationInterest } from 'src/hooks/ApplicationInterest'
import { useMobile } from 'src/hooks/Mobile'
import { getApplicantsListOrUndefined } from 'src/utils/GetApplicantsListOrFalse'

import { FormContext, ReturnContextProps } from '../Context'
import { WizardScreen } from '../Context/Type'
import MobileButton from '../MobileButton'
import QualificationFormSteps from '../Steps'
import UserStepper from '../UserStepper'
import {
  isPrimaryCenterLayout,
  isPrimaryFullWidthLayout,
  shouldShowBack,
  shouldShowHeader,
} from './Utils'

const FormWrapper: React.FC = () => {
  const { wizardContext, getCurrentStepTitle, loading, previousStep } = useContext(
    FormContext
  ) as ReturnContextProps
  const isMobile = useMobile()
  const { asset, assetRequestStatus, applicants } = useApplicationDetails()
  const { id } = useParams<{
    id: string
  }>()

  const showContent =
    (!id || (assetRequestStatus && assetRequestStatus !== FetchStatus.FAILED)) && asset

  const handleBackNavigationMobile = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault()
    previousStep()
  }

  const renderMobileFormButton = (updated: boolean, disabled: boolean): React.ReactElement => {
    return (
      <MobileButton
        disabled={disabled}
        updated={updated}
        handleBackNavigationMobile={
          shouldShowBack(wizardContext) ? handleBackNavigationMobile : undefined
        }
      />
    )
  }

  useApplicationInterest()

  if (
    (!asset && assetRequestStatus === FetchStatus.FAILED) ||
    assetRequestStatus === FetchStatus.NOT_FOUND
  ) {
    return <NotFound />
  }

  if (!asset) {
    return null
  }

  const renderHeaderBlock = (): React.ReactElement | undefined => {
    if (shouldShowHeader(wizardContext)) {
      return (
        <QualificationHeader
          step={getCurrentStepTitle()}
          mobileApplicants={getApplicantsListOrUndefined(
            applicants,
            wizardContext.context.applicantId
          )}
          onBackClicked={shouldShowBack(wizardContext) ? previousStep : undefined}
          isSmall={wizardContext.matches(WizardScreen.bookingSelector)}
          currentApplicantId={wizardContext.context.applicantId}
        />
      )
    }

    return undefined
  }

  const renderSideBarBlock = (): React.ReactElement | undefined => {
    return (
      <>
        {!isMobile && (
          <QualificationSideBar asset={asset} application={wizardContext.context.application} />
        )}
        {isMobile && (
          <QualificationQualificationSideBarMobile
            asset={asset}
            application={wizardContext.context.application}
          />
        )}
      </>
    )
  }

  const renderMainBlock = (): React.ReactElement => {
    return (
      <>
        <QualificationFormSteps
          renderMobileFormButton={isMobile ? renderMobileFormButton : undefined}
        />
        {loading && <LoaderWrapper absolute />}
      </>
    )
  }

  const hideStepper = () => {
    if (wizardContext) {
      if (
        wizardContext.matches(WizardScreen.noBookableSlot) ||
        wizardContext.matches(WizardScreen.bookingSelector) ||
        wizardContext.matches(WizardScreen.success) ||
        wizardContext.matches(WizardScreen.slotAlreadyTaken) ||
        wizardContext.matches(WizardScreen.failed) ||
        wizardContext.matches(WizardScreen.closed)
      ) {
        return true
      }
    }
    return false
  }

  const progressValue = () => {
    const { context } = wizardContext
    if (context.progress.total > 0 && context.progress.current > 1) {
      const steps = 100 / context.progress.total
      return `${steps * context.progress.current}`
    }

    return '0'
  }

  const showProgress = () => {
    if (shouldShowHeader(wizardContext)) {
      return <VisitorFormProgress size={progressValue()} />
    }

    return undefined
  }

  return (
    <QualificationLayout
      hideContent={!showContent}
      primaryFullWidth={isPrimaryFullWidthLayout(wizardContext)}
      primaryCenter={isPrimaryCenterLayout(wizardContext)}
      BlockProgress={showProgress()}
      BlockHeader={renderHeaderBlock()}
      BlockStepper={
        <UserStepper
          hide={hideStepper()}
          applicants={getApplicantsListOrUndefined(applicants, wizardContext.context.applicantId)}
          currentApplicantId={wizardContext.context.applicantId}
        />
      }
      BlockMain={renderMainBlock()}
      BlockConsent={<Consent />}
      BlockGdpr={<FormGdpr asset={asset} />}
      BlockSidebar={renderSideBarBlock()}
    />
  )
}

export default FormWrapper
